import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { isEmpty, pathOr } from 'ramda';
import { useTranslation } from 'react-i18next';

import { GET_CHAT_HISTORY } from 'apollo/queries';
import { ENSURE_CHAT_GREETING } from 'apollo/mutations';
import { useGetParams } from 'components/evieOnboarding/utils/useGetParams';
import { useLocalStorage } from 'utils/hooks/useLocalStorage';
import { useToggle } from 'utils/hooks/useToggle';
import { handleTrackEvent } from 'utils/analytics';
import { ROUTES } from 'utils/constants';
import { KEYS, USER_ROLES } from 'components/evieOnboarding/utils/constants';
import { CHAT_CONTENT_TYPES, CHAT_ROLES } from './utils';

const EvieChatContext = createContext();

// The backend guarantees a first assistant message eventually (greeting worker
// with retries + a persisted static fallback), so an empty first load means
// "the greeting is still being written", not "there is nothing to show".
const GREETING_POLL_INTERVAL_MS = 2500;
const GREETING_TIMEOUT_MS = 30000;
const GREETING_SLOW_POLL_INTERVAL_MS = 15000;
// The backend resolves every greeting (worker retries + persisted static
// fallback) within minutes; background retries past this point only waste
// requests, so the slow poll stops here rather than running forever.
const GREETING_SLOW_POLL_MAX_MS = 600000;

const EvieChatProvider = ({ children }) => {
  const {
    i18n: { language },
  } = useTranslation();
  const languageWithoutRegionCode = language.slice(0, 2);
  const [userToken, setUserToken] = useLocalStorage(KEYS.USER_TOKEN);
  const [isNative, setIsNative] = useLocalStorage(KEYS.IS_NATIVE, false);
  const [flow, setFlow] = useLocalStorage(KEYS.FLOW);
  const { urlToken, native, push } = useGetParams();
  const token = userToken || urlToken;

  const {
    error,
    loading,
    data: { chatQuota: { messagesLeft = null, unlimited = false, resetsAt } = {}, chatSessions = [] } = {},
    refetch: refetchChatHistory,
  } = useQuery(GET_CHAT_HISTORY, {
    skip: !urlToken && !userToken,
    context: {
      headers: {
        language: languageWithoutRegionCode,
        authorization: token ? `Bearer ${token}` : '',
        role: USER_ROLES.INDIVIDUAL,
      },
    },
  });
  const [ensureChatGreeting] = useMutation(ENSURE_CHAT_GREETING);
  const quickReplies = (chatSessions[0] && chatSessions[0].quickReplies) || [];
  const isQuotaExceeded = messagesLeft === 0 && !unlimited;
  const hasMessages = chatSessions.some((session) => session.messages && session.messages.length > 0);
  // A returning greeting appends *below* existing history, so "messages exist"
  // cannot be the stop condition — only "a message newer than our request" can.
  // Split by role: the user typing during the wait must not masquerade as the
  // greeting (her message, or its normal reply, is also "newer").
  const allMessages = chatSessions.flatMap((session) => session.messages || []);
  const newestTimeWhere = (predicate) =>
    allMessages
      .filter(predicate)
      .reduce((newest, message) => Math.max(newest, new Date(message.createdAt).getTime()), 0);
  const newestMessageAt = newestTimeWhere(() => true);
  const newestAssistantMessageAt = newestTimeWhere((message) => message.role === CHAT_ROLES.ASSISTANT);
  const newestUserMessageAt = newestTimeWhere((message) => message.role === CHAT_ROLES.USER);

  const [assistantStatus, setAssistantStatus] = useState(CHAT_CONTENT_TYPES.CHAT);
  const [selectedContent, setSelectedContent] = useState(null);
  const [isAwaitingResponse, toggleIsAwaitingResponse] = useToggle(false);
  const [greetingTimedOut, setGreetingTimedOut] = useState(false);
  const greetingWaitStartRef = useRef(null);
  // null until ensureChatGreeting answers: the backend owns the "is a greeting
  // due?" question, the client only picks a wait strategy from the answer.
  const [willGenerateGreeting, setWillGenerateGreeting] = useState(null);
  const ensureGreetingRequestedRef = useRef(false);
  const returningGreetingSinceRef = useRef(null);
  const returningGreetingMetaRef = useRef({});
  const hadHistoryAtGreetingRef = useRef(false);
  const returningGreetingTrackedRef = useRef(false);
  // History already on screen when the greeting was requested: the appended
  // greeting is the only thing being waited on, so the wait is a returning one.
  const isReturningWait = willGenerateGreeting === true && hadHistoryAtGreetingRef.current;

  // Derived from the URL param too: on a first webview visit isNative is still
  // false in localStorage when timers capture their closures. The ref keeps
  // long-lived callbacks (the 30s cap) reading the current value.
  const platformSource = isNative || native === 'true' ? 'webview' : 'browser';
  const analyticsSource = isReturningWait ? 'returning' : platformSource;
  const analyticsSourceRef = useRef(analyticsSource);
  analyticsSourceRef.current = analyticsSource;

  // She spoke during the wait: the worker's in-lock re-check sees her message
  // as fresh activity and drops the greeting, so the wait must end silently —
  // and her message (or its normal reply) must never count as the greeting.
  const userRepliedDuringWait =
    willGenerateGreeting === true && newestUserMessageAt > returningGreetingSinceRef.current;
  const greetingArrived =
    !userRepliedDuringWait && newestAssistantMessageAt > returningGreetingSinceRef.current;

  // An empty chat waits regardless of the mutation (the backend always has a
  // greeting due when there are no messages, and the first paint must not be
  // blocked on the round-trip); history-present waits only on an explicit yes,
  // and only until the greeting itself arrives or the user takes over.
  const isWaitingForGreeting =
    !hasMessages || (willGenerateGreeting === true && !userRepliedDuringWait && !greetingArrived);

  // Derived synchronously so the render where the query settles empty already
  // shows the typing indicator — an effect-based transition would let the
  // generic welcome flash for one frame first.
  const isAwaitingGreeting = Boolean(token) && !loading && !error && isWaitingForGreeting && !greetingTimedOut;

  useEffect(() => {
    if (!token) {
      push(ROUTES.EVIE_LOGIN);
    }
    if (urlToken) {
      setUserToken(urlToken);
    }
  }, [token]);

  // One-time cleanup: the removed 5s gate used to persist this flag.
  useEffect(() => {
    window.localStorage.removeItem('hasLoadedChatHistory');
  }, []);

  // Asked on every mount, once, after the history query settles: only then do we
  // know whether a greeting would be appended to history or fill an empty chat.
  useEffect(() => {
    if (!token || loading || ensureGreetingRequestedRef.current) return;
    ensureGreetingRequestedRef.current = true;
    ensureChatGreeting({
      context: {
        headers: {
          authorization: `Bearer ${token}`,
          role: USER_ROLES.INDIVIDUAL,
        },
      },
    })
      .then((response) => {
        const payload = pathOr({}, ['data', 'ensureChatGreeting'])(response);
        if (!isEmpty(pathOr([], ['errors'])(payload)) || !payload.willGenerate) {
          setWillGenerateGreeting(false);
          return;
        }
        // Server-time high-water mark, not Date.now(): the stop condition
        // compares against message createdAt (server clock), and a client
        // clock running ahead would otherwise never see the greeting as new.
        returningGreetingSinceRef.current = newestMessageAt;
        returningGreetingMetaRef.current = payload;
        hadHistoryAtGreetingRef.current = hasMessages;
        setWillGenerateGreeting(true);
      })
      .catch(() => {
        // A backend that cannot answer is treated exactly like "not due": the
        // chat keeps working on today's history-only behaviour.
        console.warn('ensureChatGreeting unavailable');
        setWillGenerateGreeting(false);
      });
  }, [token, loading]);

  // Measure the wait from the first render that showed the typing indicator,
  // and report it once when the greeting lands.
  useEffect(() => {
    if (isAwaitingGreeting && greetingWaitStartRef.current === null) {
      greetingWaitStartRef.current = Date.now();
    }
  }, [isAwaitingGreeting]);

  useEffect(() => {
    if (isWaitingForGreeting || greetingWaitStartRef.current === null) return;
    // Only a delivered greeting is latency. A wait the user cancelled by
    // speaking first ends without one — the backend drops it — so timing it
    // would report a duration no patient ever spent waiting for a greeting.
    if (greetingArrived) {
      handleTrackEvent('greeting_wait_ms', {
        ms: Date.now() - greetingWaitStartRef.current,
        source: analyticsSourceRef.current,
      });
    }
    greetingWaitStartRef.current = null;
  }, [isWaitingForGreeting]);

  // The win-back landing metric: fired when the returning greeting is on screen,
  // never when the mutation merely promised one, and never for the user's own
  // message or its reply (the backend drops the greeting once she has spoken).
  useEffect(() => {
    if (!isReturningWait || returningGreetingTrackedRef.current) return;
    if (!greetingArrived) return;
    returningGreetingTrackedRef.current = true;
    handleTrackEvent('Return greeting viewed', {
      stage_key: returningGreetingMetaRef.current.stageKey,
      days_dormant_bucket: returningGreetingMetaRef.current.daysDormantBucket,
      source: analyticsSourceRef.current,
    });
  }, [isReturningWait, greetingArrived]);

  // The cap is armed from mount, independent of the query lifecycle: a hung or
  // rejected initial request must still fall back at 30s, not spin forever.
  useEffect(() => {
    if (!token || !isWaitingForGreeting || greetingTimedOut) return undefined;
    const timeoutId = setTimeout(() => {
      handleTrackEvent('greeting_timeout', { source: analyticsSourceRef.current });
      setGreetingTimedOut(true);
    }, GREETING_TIMEOUT_MS);
    return () => clearTimeout(timeoutId);
  }, [token, isWaitingForGreeting, greetingTimedOut]);

  // Poll while the greeting is undelivered — including through transient
  // errors and a hung first request (Apollo dedupes in-flight fetches). After
  // the cap, drop to a slow poll so the greeting still lands if she lingers.
  useEffect(() => {
    if (!token || !isWaitingForGreeting) return undefined;
    const interval = greetingTimedOut ? GREETING_SLOW_POLL_INTERVAL_MS : GREETING_POLL_INTERVAL_MS;
    const stopAt = greetingTimedOut ? Date.now() + GREETING_SLOW_POLL_MAX_MS : null;
    const intervalId = setInterval(() => {
      if (stopAt && Date.now() > stopAt) {
        clearInterval(intervalId);
        return;
      }
      refetchChatHistory().catch(() => {});
    }, interval);
    return () => clearInterval(intervalId);
  }, [token, isWaitingForGreeting, greetingTimedOut]);

  useEffect(() => {
    if (flow === 'assistant') {
      refetchChatHistory();
      setFlow(null);
    }
  }, [flow]);

  useEffect(() => {
    if (native === 'true') {
      setIsNative(true);
    }
  }, [userToken, urlToken, native]);

  useEffect(() => {
    if (!isQuotaExceeded || !resetsAt) return undefined;
    const msUntilReset = new Date(resetsAt).getTime() - Date.now();
    const timeoutId = setTimeout(() => refetchChatHistory(), Math.max(msUntilReset, 0));
    return () => clearTimeout(timeoutId);
  }, [isQuotaExceeded, resetsAt]);

  return (
    <EvieChatContext.Provider
      value={{
        // After the cap, stop gating the chat on a hung initial request so the
        // default prompt can take over instead of an endless loader.
        loading: loading && !greetingTimedOut,
        error,
        isNative,
        isAwaitingResponse,
        isAwaitingGreeting,
        isQuotaExceeded,
        unlimited,
        toggleIsAwaitingResponse,
        refetchChatHistory,
        chatSessions,
        quickReplies,
        messagesLeft,
        assistantStatus,
        changeAssistantStatus: setAssistantStatus,
        selectedContent,
        selectContent: setSelectedContent,
      }}
    >
      {children}
    </EvieChatContext.Provider>
  );
};

EvieChatProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

function useEvieChatContext() {
  const context = useContext(EvieChatContext);
  if (!context) {
    throw new Error('Expected an EvieChatContext somewhere in the react tree to set context value');
  }
  return context;
}

export { useEvieChatContext, EvieChatProvider };
